/* Locale-aware formatting.
 *
 * Currency codes are never written here. They arrive on the listing row or,
 * failing that, on the active country row -- both backend values. If neither
 * supplies one, the amount renders bare rather than guessing a currency.
 *
 * Digits stay Western in both locales. Arabic-Indic numerals are correct
 * Arabic typography in prose, but prices, areas and phone codes here are read
 * against a backend that emits Western digits, and mixing the two forms in one
 * interface costs more legibility than it buys authenticity. The Arabic locale
 * is therefore requested with the latn numbering system explicitly.
 */

import type { Locale } from "@/i18n/strings";

const INTL_LOCALE: Record<Locale, string> = {
  ar: "ar-EG-u-nu-latn",
  en: "en-GB",
};

export function formatNumber(value: number, locale: Locale): string {
  try {
    return new Intl.NumberFormat(INTL_LOCALE[locale]).format(value);
  } catch {
    return String(value);
  }
}

/**
 * Format a price. `currency` is whatever the backend supplied; when it is
 * missing the number is rendered without a currency rather than defaulted.
 */
export function formatPrice(
  value: number,
  currency: string | null,
  locale: Locale,
): string {
  if (!currency) return formatNumber(value, locale);
  try {
    return new Intl.NumberFormat(INTL_LOCALE[locale], {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    // An unrecognised ISO code must not break the card.
    return `${formatNumber(value, locale)} ${currency}`;
  }
}

/** "District, City" in the active language, skipping whichever part is absent. */
export function locationLabel(
  district: string | null,
  city: string | null,
  locale: Locale,
): string {
  const parts = [district, city].filter((p): p is string => !!p && p.trim() !== "");
  if (parts.length === 0) return "";
  return parts.join(locale === "ar" ? "، " : ", ");
}
